import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { calculateSPRSScore } from './data/controlWeights';

// ── Framework labels ──────────────────────────────────────────────────────────
const FRAMEWORK_LABELS = {
  cmmc_l1:  'CMMC 2.0 — Level 1 Foundational',
  cmmc_l2:  'CMMC 2.0 — Level 2 Advanced',
  cmmc_l3:  'CMMC 2.0 — Level 3 Expert',
  nist_171: 'NIST SP 800-171',
  soc2:     'SOC 2 Type II',
  iso27001: 'ISO/IEC 27001:2022',
  hipaa:    'HIPAA Security Rule',
  nist_csf: 'NIST CSF 2.0',
  pci_dss:  'PCI DSS v4.0',
  fedramp:  'FedRAMP Moderate',
};

const CMMC_FRAMEWORKS = ['cmmc_l1', 'cmmc_l2', 'cmmc_l3', 'nist_171'];

function frameworkLabel(fw) {
  return FRAMEWORK_LABELS[fw] || (fw ? fw.toUpperCase() : 'Unknown');
}

// ── Domain helpers ─────────────────────────────────────────────────────────────
function computeDomainStats(questions, answers) {
  const map = {};
  questions.forEach(q => {
    if (!map[q.domain]) map[q.domain] = { name: q.domain, met: 0, total: 0 };
    map[q.domain].total++;
    if (answers[q.id] === 'yes') map[q.domain].met++;
  });
  return Object.values(map).map(d => ({
    ...d,
    pct: d.total > 0 ? Math.round((d.met / d.total) * 100) : 0,
  }));
}

function domainStatusLabel(pct) {
  return pct >= 80 ? 'On Track' : pct >= 50 ? 'In Progress' : 'At Risk';
}

function getNistRef(controlId) {
  if (!controlId) return '';
  const m = controlId.match(/(\d+\.\d+\.\d+)/);
  return m ? m[1] : '';
}

// ── Status helpers ─────────────────────────────────────────────────────────────
function answerLabel(answer) {
  if (answer === 'yes')     return 'Implemented';
  if (answer === 'partial') return 'Partial';
  if (answer === 'no')      return 'Not Implemented';
  return 'Not Answered';
}

function statusBgFg(statusOrAnswer) {
  const s = (statusOrAnswer || '').toLowerCase();
  if (s === 'yes' || s === 'implemented' || s === 'compliant' || s === 'on track' || s === 'current' || s === 'approved' || s === 'closed')
    return { bg: 'D1FAE5', fg: '065F46' };
  if (s === 'partial' || s === 'partially compliant' || s === 'in progress' || s === 'expiring soon')
    return { bg: 'FEF3C7', fg: '92400E' };
  if (s === 'no' || s === 'not implemented' || s === 'non-compliant' || s === 'at risk' || s === 'open' || s === 'expired' || s === 'overdue')
    return { bg: 'FEE2E2', fg: '991B1B' };
  if (s === 'not answered' || s === 'draft')
    return { bg: 'F3F4F6', fg: '6B7280' };
  return null;
}

function priorityBgFg(priority) {
  const p = (priority || '').toUpperCase().replace('MEDIUM', 'MED');
  if (p === 'HIGH') return { bg: 'FEE2E2', fg: '991B1B' };
  if (p === 'MED')  return { bg: 'FEF3C7', fg: '92400E' };
  if (p === 'LOW')  return { bg: 'D1FAE5', fg: '065F46' };
  return { bg: 'F3F4F6', fg: '6B7280' };
}

function scoreFg(pct) {
  if (pct >= 80) return '065F46';
  if (pct >= 50) return '92400E';
  return '991B1B';
}

// ── Cell style builders ───────────────────────────────────────────────────────
const THIN_BORDER = {
  top:    { style: 'thin', color: { rgb: 'D1D5DB' } },
  bottom: { style: 'thin', color: { rgb: 'D1D5DB' } },
  left:   { style: 'thin', color: { rgb: 'D1D5DB' } },
  right:  { style: 'thin', color: { rgb: 'D1D5DB' } },
};

function navyHdr(sz = 11, align = 'left') {
  return {
    font: { name: 'Calibri', sz, bold: true, color: { rgb: 'FFFFFF' } },
    fill: { patternType: 'solid', fgColor: { rgb: '0F3460' } },
    alignment: { vertical: 'center', horizontal: align, wrapText: false },
    border: THIN_BORDER,
  };
}

function accentHdr(sz = 11) {
  return {
    font: { name: 'Calibri', sz, bold: true, color: { rgb: 'FFFFFF' } },
    fill: { patternType: 'solid', fgColor: { rgb: '2D7DD2' } },
    alignment: { vertical: 'center', horizontal: 'left', wrapText: false },
    border: THIN_BORDER,
  };
}

function lightGrayHdr(sz = 11) {
  return {
    font: { name: 'Calibri', sz, bold: false, color: { rgb: '1A1A2E' } },
    fill: { patternType: 'solid', fgColor: { rgb: 'F3F4F6' } },
    alignment: { vertical: 'center', horizontal: 'left', wrapText: false },
    border: THIN_BORDER,
  };
}

function labelStyle() {
  return {
    font: { name: 'Calibri', sz: 11, bold: true, color: { rgb: '0F3460' } },
    fill: { patternType: 'solid', fgColor: { rgb: 'F3F4F6' } },
    alignment: { vertical: 'center', horizontal: 'left', wrapText: false },
    border: THIN_BORDER,
  };
}

function dataStyle(bgHex = 'FFFFFF', bold = false, fgHex = '1A1A2E', align = 'left', wrap = false) {
  return {
    font: { name: 'Calibri', sz: 11, bold, color: { rgb: fgHex } },
    fill: { patternType: 'solid', fgColor: { rgb: bgHex } },
    alignment: { vertical: 'center', horizontal: align, wrapText: wrap },
    border: THIN_BORDER,
  };
}

function coloredDataStyle(bg, fg, bold = false, align = 'center') {
  return {
    font: { name: 'Calibri', sz: 11, bold, color: { rgb: fg } },
    fill: { patternType: 'solid', fgColor: { rgb: bg } },
    alignment: { vertical: 'center', horizontal: align, wrapText: false },
    border: THIN_BORDER,
  };
}

// Alternating row background
function altBg(i) { return i % 2 === 0 ? 'FFFFFF' : 'FAFAFA'; }

// ── Cell object helper ─────────────────────────────────────────────────────────
// Returns { v, s } cell object or plain value if no style needed
function cell(value, style) {
  const v = value === null || value === undefined ? '' : value;
  if (!style) return v;
  return { v, s: style };
}

// ── Sheet builder ─────────────────────────────────────────────────────────────
function buildSheet(rows, opts = {}) {
  // Separate plain values from styled cells
  const plainRows = rows.map(row =>
    row.map(c => (c && typeof c === 'object' && 'v' in c ? (c.v ?? '') : (c ?? '')))
  );

  const ws = XLSX.utils.aoa_to_sheet(plainRows);

  // Apply styles
  rows.forEach((row, ri) => {
    row.forEach((c, ci) => {
      if (c && typeof c === 'object' && 's' in c) {
        const addr = XLSX.utils.encode_cell({ r: ri, c: ci });
        if (ws[addr]) {
          ws[addr].s = c.s;
        } else {
          const v = c.v ?? '';
          ws[addr] = { v, t: typeof v === 'number' ? 'n' : 's', s: c.s };
        }
      }
    });
  });

  if (opts.cols)        ws['!cols']       = opts.cols.map(w => ({ wch: w }));
  if (opts.freeze)      ws['!views']      = [{ state: 'frozen', xSplit: 0, ySplit: opts.freeze }];
  if (opts.merges)      ws['!merges']     = opts.merges;
  if (opts.autoFilter)  ws['!autofilter'] = { ref: ws['!ref'] || 'A1' };

  return ws;
}

// ── SHEET 1 — Dashboard ───────────────────────────────────────────────────────
function buildDashboard(questions, answers, profile, framework, company) {
  const isCmmc       = CMMC_FRAMEWORKS.includes(framework);
  const sprsScore    = isCmmc ? calculateSPRSScore(answers) : null;
  const total        = questions.length;
  const yesCount     = questions.filter(q => answers[q.id] === 'yes').length;
  const partialCount = questions.filter(q => answers[q.id] === 'partial').length;
  const noCount      = questions.filter(q => answers[q.id] === 'no').length;
  const naCount      = questions.filter(q => !answers[q.id]).length;
  const completion   = total > 0 ? Math.round(((yesCount + partialCount * 0.5) / total) * 100) : 0;
  const orgName      = company?.name || profile?.company_name || 'Organization';
  const fwLabel      = frameworkLabel(framework);
  const today        = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const domains      = computeDomainStats(questions, answers);

  const rows = [];
  const merges = [];

  // ── Header block (rows 0-3, display rows 1-4) ─────────────────────────────
  // Row 0: "TheClearAudit — System Security Plan" — merged A-F, navy bg, white bold, sz 16
  rows.push([
    cell(`TheClearAudit — System Security Plan  |  ${today}`, navyHdr(16, 'left')),
    cell('', navyHdr(16)), cell('', navyHdr(16)),
    cell('', navyHdr(16)), cell('', navyHdr(16)), cell('', navyHdr(16)),
  ]);
  merges.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } });

  // Row 1: company name — merged A-F, accent bg, sz 13
  rows.push([
    cell(orgName, accentHdr(13)),
    cell('', accentHdr(13)), cell('', accentHdr(13)),
    cell('', accentHdr(13)), cell('', accentHdr(13)), cell('', accentHdr(13)),
  ]);
  merges.push({ s: { r: 1, c: 0 }, e: { r: 1, c: 5 } });

  // Row 2: framework — merged A-F, light gray bg, sz 11
  rows.push([
    cell(fwLabel, lightGrayHdr(11)),
    cell('', lightGrayHdr(11)), cell('', lightGrayHdr(11)),
    cell('', lightGrayHdr(11)), cell('', lightGrayHdr(11)), cell('', lightGrayHdr(11)),
  ]);
  merges.push({ s: { r: 2, c: 0 }, e: { r: 2, c: 5 } });

  // Row 3: blank
  rows.push(['', '', '', '', '', '']);

  // ── Assessment Summary (rows 4-11, display rows 5-12) ────────────────────
  // Row 4: "ASSESSMENT SUMMARY" header — merged A-B
  rows.push([
    cell('ASSESSMENT SUMMARY', navyHdr(11)),
    cell('', navyHdr(11)),
    '', '', '', '',
  ]);
  merges.push({ s: { r: 4, c: 0 }, e: { r: 4, c: 1 } });

  // Row 5: SPRS Score
  const sprsDisplay = isCmmc ? `${sprsScore} / 110` : 'N/A';
  rows.push([
    cell('SPRS Score', labelStyle()),
    cell(sprsDisplay, dataStyle('FFFFFF', true, isCmmc ? '2D7DD2' : '6B7280')),
    '', '', '', '',
  ]);

  // Row 6: Total Controls
  rows.push([cell('Total Controls', labelStyle()), cell(total, dataStyle()), '', '', '', '']);

  // Row 7: Implemented
  rows.push([cell('Implemented', labelStyle()), cell(yesCount, coloredDataStyle('D1FAE5', '065F46', true)), '', '', '', '']);

  // Row 8: Partial
  rows.push([cell('Partial', labelStyle()), cell(partialCount, coloredDataStyle('FEF3C7', '92400E', true)), '', '', '', '']);

  // Row 9: Not Implemented
  rows.push([cell('Not Implemented', labelStyle()), cell(noCount, coloredDataStyle('FEE2E2', '991B1B', true)), '', '', '', '']);

  // Row 10: Not Answered
  rows.push([cell('Not Answered', labelStyle()), cell(naCount, coloredDataStyle('F3F4F6', '6B7280', false)), '', '', '', '']);

  // Row 11: Overall Completion
  rows.push([cell('Overall Completion', labelStyle()), cell(`${completion}%`, dataStyle('FFFFFF', true, completion >= 80 ? '065F46' : completion >= 50 ? '92400E' : '991B1B')), '', '', '', '']);

  // Row 12: blank
  rows.push(['', '', '', '', '', '']);

  // ── Domain Readiness (row 13+, display rows 14+) ─────────────────────────
  // Row 13: "DOMAIN READINESS" header — merged A-D
  rows.push([
    cell('DOMAIN READINESS', navyHdr(11)),
    cell('', navyHdr(11)), cell('', navyHdr(11)), cell('', navyHdr(11)),
    '', '',
  ]);
  merges.push({ s: { r: 13, c: 0 }, e: { r: 13, c: 3 } });

  // Row 14: column headers
  rows.push([
    cell('Domain', accentHdr(11)),
    cell('Score', accentHdr(11)),
    cell('Controls Met', accentHdr(11)),
    cell('Status', accentHdr(11)),
    '', '',
  ]);

  // Domain data rows
  domains.forEach((d, i) => {
    const lbl     = domainStatusLabel(d.pct);
    const colors  = statusBgFg(lbl);
    const sfg     = scoreFg(d.pct);
    const rowBg   = altBg(i);
    rows.push([
      cell(d.name,              dataStyle(rowBg, true,  '1A1A2E')),
      cell(`${d.pct}%`,        dataStyle(rowBg, true,  sfg,    'center')),
      cell(`${d.met} / ${d.total}`, dataStyle(rowBg, false, '1A1A2E', 'center')),
      colors
        ? cell(lbl, coloredDataStyle(colors.bg, colors.fg, true, 'center'))
        : cell(lbl, dataStyle(rowBg)),
      '', '',
    ]);
  });

  return buildSheet(rows, {
    cols: [30, 15, 15, 15, 15, 20],
    merges,
  });
}

// ── SHEET 2 — Control Assessment ──────────────────────────────────────────────
function buildControlSheet(questions, answers, evidence, narratives, framework) {
  const isCmmc = CMMC_FRAMEWORKS.includes(framework);

  // Build evidence count by control
  const evByControl = {};
  (evidence || []).forEach(ev => {
    const ctrl = ev.control_id || ev.controlId || ev.linked_control || ev.control || '';
    if (ctrl) evByControl[ctrl] = (evByControl[ctrl] || 0) + 1;
  });

  // Build narrative map
  const narMap = {};
  (narratives || []).forEach(n => { narMap[n.control_id] = n.narrative || ''; });

  // Header row
  const headerStyle = navyHdr(11);
  const headerRow = [
    cell('Control ID',            headerStyle),
    cell('NIST Reference',        headerStyle),
    cell('Domain',                headerStyle),
    cell('Full Requirement',      headerStyle),
    cell('Status',                headerStyle),
    cell('Implementation Notes',  headerStyle),
    cell('Evidence Count',        headerStyle),
    cell('Last Updated',          headerStyle),
  ];

  const dataRows = questions.map((q, i) => {
    const ans       = answers[q.id];
    const label     = answerLabel(ans);
    const colors    = statusBgFg(ans);
    const narrative = narMap[q.id] || '';
    const evCount   = evByControl[q.id] || 0;
    const rowBg     = altBg(i);
    const nistRef   = isCmmc ? getNistRef(q.id) : '';

    return [
      cell(q.id    || '',    dataStyle(rowBg, true, '0F3460')),
      cell(nistRef,          dataStyle(rowBg, false, '6B7280')),
      cell(q.domain || '',   dataStyle(rowBg, false, '6B7280')),
      cell(q.text   || '',   dataStyle(rowBg, false, '1A1A2E', 'left', true)),
      colors
        ? cell(label, coloredDataStyle(colors.bg, colors.fg, true, 'center'))
        : cell(label, dataStyle(rowBg, false, '6B7280', 'center')),
      cell(narrative,        dataStyle(rowBg, false, '1A1A2E', 'left', true)),
      cell(evCount,          dataStyle(rowBg, false, '1A1A2E', 'center')),
      cell('',               dataStyle(rowBg)),
    ];
  });

  return buildSheet([headerRow, ...dataRows], {
    cols: [15, 15, 8, 80, 20, 50, 15, 20],
    freeze: 1,
    autoFilter: true,
  });
}

// ── SHEET 3 — POA&M ───────────────────────────────────────────────────────────
function buildPoamSheet(tasks) {
  const openTasks = (tasks || []).filter(t => !t.done);
  const today = new Date();

  const headerStyle = navyHdr(11);
  const headerRow = [
    cell('Item ID',            headerStyle),
    cell('Control ID',         headerStyle),
    cell('Domain',             headerStyle),
    cell('Weakness Description', headerStyle),
    cell('Priority',           headerStyle),
    cell('Assigned Owner',     headerStyle),
    cell('Due Date',           headerStyle),
    cell('Status',             headerStyle),
    cell('Milestone Notes',    headerStyle),
  ];

  const dataRows = openTasks.map((t, i) => {
    const itemId   = `POA&M-${String(i + 1).padStart(3, '0')}`;
    const priority = (t.priority || 'low').toUpperCase().replace('MEDIUM', 'MED');
    const pColors  = priorityBgFg(priority);
    const status   = t.done ? 'Closed' : 'Open';
    const sColors  = statusBgFg(status);
    const rowBg    = altBg(i);

    let dueDateStr = '';
    if (t.due) {
      try {
        const d = new Date(t.due);
        if (!isNaN(d.getTime())) {
          dueDateStr = d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
          // Overdue = past due
          if (d < today) sColors && (sColors.bg = 'FEE2E2') && (sColors.fg = '991B1B');
        } else {
          dueDateStr = String(t.due);
        }
      } catch (e) {
        dueDateStr = String(t.due);
      }
    }

    return [
      cell(itemId,                        dataStyle(rowBg, true, '0F3460')),
      cell(t.domain        || '',         dataStyle(rowBg, false, '6B7280')),
      cell(t.domain        || '',         dataStyle(rowBg, false, '6B7280')),
      cell(t.name || t.title || '',       dataStyle(rowBg, false, '1A1A2E', 'left', true)),
      pColors ? cell(priority, coloredDataStyle(pColors.bg, pColors.fg, true, 'center')) : cell(priority, dataStyle(rowBg)),
      cell(t.owner         || '',         dataStyle(rowBg)),
      cell(dueDateStr,                    dataStyle(rowBg, false, '1A1A2E', 'center')),
      sColors ? cell(status, coloredDataStyle(sColors.bg, sColors.fg, true, 'center')) : cell(status, dataStyle(rowBg, false, '6B7280', 'center')),
      cell(t.notes         || '',         dataStyle(rowBg, false, '6B7280', 'left', true)),
    ];
  });

  const emptyRow = dataRows.length === 0
    ? [[cell('No open action items.', dataStyle('F3F4F6', false, '6B7280', 'center')), '', '', '', '', '', '', '', '']]
    : [];

  return buildSheet([headerRow, ...dataRows, ...emptyRow], {
    cols: [12, 15, 10, 50, 12, 20, 15, 15, 40],
    freeze: 1,
    autoFilter: true,
  });
}

// ── SHEET 4 — Evidence Inventory ──────────────────────────────────────────────
function buildEvidenceSheet(evidence) {
  const evidenceList = evidence || [];
  const today = new Date();

  const headerStyle = navyHdr(11);
  const headerRow = [
    cell('File Name',      headerStyle),
    cell('Mapped Control', headerStyle),
    cell('Date Added',     headerStyle),
    cell('Expiry Date',    headerStyle),
    cell('File Size',      headerStyle),
    cell('Status',         headerStyle),
  ];

  const dataRows = evidenceList.map((ev, i) => {
    const rowBg = altBg(i);

    let evStatus = ev.status || 'Draft';
    let expiryDisplay = '';
    if (ev.expires_at) {
      const daysLeft = Math.floor((new Date(ev.expires_at) - today) / 86400000);
      try {
        expiryDisplay = new Date(ev.expires_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      } catch (e) {
        expiryDisplay = String(ev.expires_at);
      }
      if (daysLeft < 0)       evStatus = 'Expired';
      else if (daysLeft <= 30) evStatus = 'Expiring Soon';
      else                     evStatus = 'Current';
    }

    const sColors = statusBgFg(evStatus);

    let dateAdded = ev.date || ev.created_at || '';
    if (dateAdded) {
      try {
        const d = new Date(dateAdded);
        if (!isNaN(d.getTime())) {
          dateAdded = d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        }
      } catch (e) { /* use raw */ }
    }

    return [
      cell(ev.name    || '',  dataStyle(rowBg, true,  '1A1A2E')),
      cell(ev.control || ev.control_id || '',  dataStyle(rowBg, false, '6B7280')),
      cell(dateAdded,         dataStyle(rowBg, false, '1A1A2E', 'center')),
      cell(expiryDisplay,     dataStyle(rowBg, false, expiryDisplay && evStatus === 'Expired' ? '991B1B' : expiryDisplay && evStatus === 'Expiring Soon' ? '92400E' : '1A1A2E', 'center')),
      cell(ev.size    || '',  dataStyle(rowBg, false, '6B7280', 'center')),
      sColors
        ? cell(evStatus, coloredDataStyle(sColors.bg, sColors.fg, true, 'center'))
        : cell(evStatus, dataStyle(rowBg, false, '6B7280', 'center')),
    ];
  });

  const emptyRow = dataRows.length === 0
    ? [[cell('No evidence files uploaded.', dataStyle('F3F4F6', false, '6B7280', 'center')), '', '', '', '', '']]
    : [];

  return buildSheet([headerRow, ...dataRows, ...emptyRow], {
    cols: [40, 20, 18, 18, 12, 18],
    freeze: 1,
    autoFilter: true,
  });
}

// ── Main export ───────────────────────────────────────────────────────────────
export function generateSSPSpreadsheet(questions, answers, profile, tasks, evidence, framework, narratives, company) {
  const safeQuestions  = questions  || [];
  const safeAnswers    = answers    || {};
  const safeProfile    = profile    || {};
  const safeTasks      = tasks      || [];
  const safeEvidence   = evidence   || [];
  const safeFramework  = framework  || '';
  const safeNarratives = narratives || [];
  const safeCompany    = company    || {};

  const wb = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(wb, buildDashboard(safeQuestions, safeAnswers, safeProfile, safeFramework, safeCompany), 'Dashboard');
  XLSX.utils.book_append_sheet(wb, buildControlSheet(safeQuestions, safeAnswers, safeEvidence, safeNarratives, safeFramework), 'Control Assessment');
  XLSX.utils.book_append_sheet(wb, buildPoamSheet(safeTasks), 'POA&M');
  XLSX.utils.book_append_sheet(wb, buildEvidenceSheet(safeEvidence), 'Evidence Inventory');

  const wbout   = XLSX.write(wb, { bookType: 'xlsx', type: 'array', cellStyles: true });
  const blob    = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const orgSlug = (safeCompany?.name || safeProfile?.company_name || 'SSP').replace(/[^a-zA-Z0-9]/g, '_');
  const dateStr = new Date().toISOString().split('T')[0];
  saveAs(blob, `TheClearAudit_SSP_${orgSlug}_${dateStr}.xlsx`);
}
